import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useGetContactCustomFieldsApi } from "src/api/customFields";
import { ContactCustomField, ContactDefaultCustomField } from "src/constants/types";
import { getCurrentCompanyId } from "src/modules/companies/slices";
import { Contact } from "src/modules/contacts/types";
import { Group } from "src/modules/groups/types";

interface Data {
  contactData?: Contact | null;
  groupData?: Group | null;
  customFields?: ContactDefaultCustomField[] | null;
}

export interface CreateContactModalController extends Data {
  show: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  open: (data?: Data) => void;
}

export const useCreateContactModalController = (): CreateContactModalController => {
  const selectedCompanyId = useSelector(getCurrentCompanyId) as string;

  const getCustomFields = useGetContactCustomFieldsApi();

  const [show, setShowModal] = useState(false);
  const [contactData, setContactData] = useState<Contact | undefined | null>();
  const [groupData, setGroupData] = useState<Group | undefined | null>();
  const [customFields, setCustomFields] = useState<ContactDefaultCustomField[] | undefined | null>();

  const open = useCallback(async (data?: Data) => {
    try {
      let customFields = data?.customFields as ContactDefaultCustomField[];

      if (!customFields) {
        const response = await getCustomFields({
          companyId: selectedCompanyId!,
          customOnly: true,
        });
        customFields = response.data.items;
      }

      if (data?.contactData?.metadata) {
        const metadata: ContactCustomField[] = customFields.map((c) => {
          const field = data?.contactData?.metadata.find((m) => m.metadataId === c.metadataId);

          return (
            field || {
              id: "",
              contactId: data.contactData!.id,
              metadataId: c.metadataId!,
              type: c.type,
              name: c.name,
              description: c.description,
              value: "",
            }
          );
        });

        setContactData({ ...data.contactData, metadata });
      } else {
        setContactData(data?.contactData);
      }

      setGroupData(data?.groupData);
      setCustomFields(customFields);
      setShowModal(true);
    } catch (e) {
      console.log(e);
    }
  }, []);

  return {
    show,
    setShowModal,
    open,
    contactData,
    groupData,
    customFields,
  };
};
